import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding, withViewTransitions } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { EffectsModule } from '@ngrx/effects';
import { routerReducer } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { L10nIntlModule, L10nTranslationModule } from 'angular-l10n';
import { InfoStoreModule } from '@wonder-core-ng/info-store/info-store.module';
import { AuthHttpInterceptor } from '@wonder-core-ng/session-store/auth-http.interceptor.service';
import { SessionStoreModule } from '@wonder-core-ng/session-store/session-store.module';
import { AIStoreModule } from '@wonder-bi-front/ai-store/ai-store.module';
import { routes } from './app.routes';
import { TenantSelectionService } from './session/tenant-selection.service';
import { BI_CORE_CONFIG } from '@wonder-bi-front/config';
import { biCoreConfigFactory } from './bi-core-config.factory';
import { FEATURE_FLAGS_PROVIDERS } from './feature-flags/feature-flags.providers';
import { ConditionStoreModule } from '@wonder-bi-front/condition-store/condition-store.module';
import { DashboardStoreModule } from '@wonder-bi-front/dashboard-store/dashboard-store.module';
import { NotificationStoreModule } from '@wonder-bi-front/notification-store/notification-store.module';
import { environment } from './environment/environment';
import { l10nConfig } from './l10n/l10n-config';
import { HttpTranslationLoader } from './l10n/translation-loader.service';
import { QueryConfigStoreModule } from '@wonder-bi-front/query-config-store/query-config-store.module';
import { QueryEngineModule } from '@wonder-bi-front/query-engine/query-engine.module';
import { DataTypePipesService } from '@wonder-bi-front/shared/data-type.pipe.service';
import { provideWbiPaginatorIntl } from '@wonder-bi-front/shared/paginator-intl';

export const appConfig: ApplicationConfig = {
  providers: [
    provideWbiPaginatorIntl(),
    provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
    DataTypePipesService,
    { provide: BI_CORE_CONFIG, useFactory: biCoreConfigFactory },
    // provideBiCore would register them; this tenant builds BI_CORE_CONFIG itself, so it registers them here.
    FEATURE_FLAGS_PROVIDERS,
    provideAppInitializer(() => inject(TenantSelectionService).restore()),
    provideRouter(routes, withComponentInputBinding(), withViewTransitions()),

    provideAnimations(),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.deployed,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthHttpInterceptor,
      multi: true,
    },
    { provide: MAT_DATE_LOCALE, useValue: 'es' },
    importProvidersFrom(
      StoreModule.forRoot({ router: routerReducer }),
      StoreDevtoolsModule.instrument({
        maxAge: 25, // Retains last 25 states
        logOnly: environment.deployed, // Restrict extension to log-only mode
        autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      }),
      EffectsModule.forRoot([]),
      SessionStoreModule,
      InfoStoreModule,
      QueryEngineModule,
      QueryConfigStoreModule,
      DashboardStoreModule,
      ConditionStoreModule,
      // Root, not per route: the header bell reads notifications on every screen.
      NotificationStoreModule,
      AIStoreModule,
      L10nTranslationModule.forRoot(l10nConfig, {
        translationLoader: HttpTranslationLoader,
      }),
      L10nIntlModule
    ),
  ],
};
